import { Fragment, useEffect, useMemo, useState } from "react";
import {
  cancelDirectDebitBatchApi,
  createDirectDebitBatch,
  deleteDirectDebitBatchApi,
  directDebitBatchFileUrl,
  fetchCompanies,
  fetchDirectDebitBatches,
  fetchOpenCustomerInvoicesForDD,
  postDirectDebitBatch,
  type Company,
  type DirectDebitBatch,
  type DirectDebitBatchLine,
  type HanaOpenCustomerInvoiceRow,
} from "../api";

/** Etiqueta para facturas sin método de pago asignado (no debería darse,
 *  ya que el backend solo devuelve facturas con OPYM.DirectDbt CORE/B2B). */
const SIN_METODO = "(sin método)";

type ColId =
  | "check"
  | "client"
  | "docNum"
  | "ref"
  | "docDate"
  | "dueDate"
  | "payMethod"
  | "scheme"
  | "total"
  | "pending"
  | "toCollect"
  | "concept";

type ColDef = { id: ColId; label: string; defaultWidth: number; alignRight?: boolean };

const INVOICE_COLUMNS: ColDef[] = [
  { id: "check", label: "", defaultWidth: 32 },
  { id: "client", label: "Cliente", defaultWidth: 280 },
  { id: "docNum", label: "Nº fra SAP", defaultWidth: 90 },
  { id: "ref", label: "Ref. cliente", defaultWidth: 120 },
  { id: "docDate", label: "F. factura", defaultWidth: 100 },
  { id: "dueDate", label: "Vencimiento", defaultWidth: 110 },
  { id: "payMethod", label: "Método cobro", defaultWidth: 140 },
  { id: "scheme", label: "Modalidad", defaultWidth: 80 },
  { id: "total", label: "Total", defaultWidth: 100, alignRight: true },
  { id: "pending", label: "Pendiente", defaultWidth: 110, alignRight: true },
  { id: "toCollect", label: "A cobrar", defaultWidth: 110, alignRight: true },
  { id: "concept", label: "Concepto SEPA", defaultWidth: 200 },
];

const INVOICE_COLS_STORAGE_KEY = "collectionBatches.invoiceColumnWidths.v1";

function loadColumnWidths(): Record<ColId, number> {
  const defaults: Record<string, number> = Object.fromEntries(
    INVOICE_COLUMNS.map((c) => [c.id, c.defaultWidth]),
  );
  try {
    const raw = localStorage.getItem(INVOICE_COLS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      for (const [k, v] of Object.entries(parsed)) {
        if (typeof v === "number" && v >= 32 && v <= 800) defaults[k] = v;
      }
    }
  } catch {
    /* localStorage bloqueado o valor corrupto — usa defaults */
  }
  return defaults as Record<ColId, number>;
}

function ResizableTh({
  width,
  onResize,
  align,
  children,
}: {
  width: number;
  onResize: (newWidth: number) => void;
  align?: "left" | "right";
  children: React.ReactNode;
}): JSX.Element {
  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;
    const onMove = (ev: MouseEvent) => {
      const next = Math.max(40, Math.min(800, startWidth + (ev.clientX - startX)));
      onResize(next);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  return (
    <th style={{ position: "relative", textAlign: align === "right" ? "right" : "left" }}>
      {children}
      <div
        onMouseDown={onMouseDown}
        title="Arrastra para cambiar el ancho de la columna"
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: 6,
          cursor: "col-resize",
          userSelect: "none",
        }}
      />
    </th>
  );
}

/**
 * Cobros por domiciliación: seleccionas empresa + fecha de cobro, marcas
 * facturas de cliente abiertas con método de adeudo directo (CORE o B2B),
 * y generas el fichero SEPA (pain.008.001.02) para presentar al banco.
 * Todas las facturas de un mismo fichero deben compartir modalidad
 * (CORE/B2B) y banco propio del método (OPYM.DflIBAN) — si el usuario
 * mezcla, se avisa y se bloquea la generación.
 *
 * Ciclo de vida tras generar: Contabilizar (crea el efecto en OBOE +
 * salda la factura). A partir de ahí — presentar el efecto al banco y
 * marcarlo cobrado — se hace directamente en SAP → Gestión bancaria →
 * Efectos: el Service Layer de esta instalación rechaza esa transición
 * por API de forma consistente, así que no se automatiza desde la app.
 */
export function CollectionBatches(): JSX.Element {
  useEffect(() => {
    document.body.classList.add("main-wide");
    return () => document.body.classList.remove("main-wide");
  }, []);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [invoices, setInvoices] = useState<HanaOpenCustomerInvoiceRow[] | null>(null);
  const [batches, setBatches] = useState<DirectDebitBatch[]>([]);
  const [selection, setSelection] = useState<Map<number, { amount: number; remittance: string }>>(new Map());
  const [collectionDate, setCollectionDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [activePayMethods, setActivePayMethods] = useState<Set<string>>(new Set());

  const [columnWidths, setColumnWidths] = useState<Record<ColId, number>>(() => loadColumnWidths());
  useEffect(() => {
    try {
      localStorage.setItem(INVOICE_COLS_STORAGE_KEY, JSON.stringify(columnWidths));
    } catch {
      /* ignora fallos de storage */
    }
  }, [columnWidths]);
  const setColWidth = (id: ColId, w: number) =>
    setColumnWidths((prev) => ({ ...prev, [id]: w }));
  const resetColWidths = () =>
    setColumnWidths(
      Object.fromEntries(INVOICE_COLUMNS.map((c) => [c.id, c.defaultWidth])) as Record<ColId, number>,
    );

  useEffect(() => {
    (async () => {
      try {
        const c = await fetchCompanies();
        const withSap = c.items.filter((x) => x.sap);
        setCompanies(withSap);
        if (withSap.length > 0 && !selectedCompany) setSelectedCompany(withSap[0]!.id);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setInvoices(null);
    setSelection(new Map());
    setError(null);
    setNotice(null);
    if (selectedCompany) void reloadBatches();
    else setBatches([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  const reloadBatches = async () => {
    if (!selectedCompany) return;
    try {
      const r = await fetchDirectDebitBatches(selectedCompany);
      setBatches(r.items);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const loadInvoices = async () => {
    if (!selectedCompany) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await fetchOpenCustomerInvoicesForDD(selectedCompany);
      setInvoices(r.items);
      setSelection(new Map());
      setActivePayMethods(new Set(r.items.map((inv) => inv.PayMethod || SIN_METODO)));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const payMethodStats = useMemo(() => {
    if (!invoices) return [] as Array<{ method: string; scheme: string; count: number }>;
    const counts = new Map<string, { scheme: string; count: number }>();
    for (const r of invoices) {
      const m = r.PayMethod || SIN_METODO;
      const cur = counts.get(m);
      if (cur) cur.count += 1;
      else counts.set(m, { scheme: r.DirectDbt, count: 1 });
    }
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0], "es"))
      .map(([method, v]) => ({ method, scheme: v.scheme, count: v.count }));
  }, [invoices]);

  const visibleInvoices = useMemo(() => {
    if (!invoices) return null;
    return invoices.filter((r) => activePayMethods.has(r.PayMethod || SIN_METODO));
  }, [invoices, activePayMethods]);

  const togglePayMethod = (method: string) => {
    const next = new Set(activePayMethods);
    if (next.has(method)) {
      next.delete(method);
      if (invoices) {
        const nextSel = new Map(selection);
        for (const r of invoices) {
          if ((r.PayMethod || SIN_METODO) === method) nextSel.delete(r.DocEntry);
        }
        setSelection(nextSel);
      }
    } else {
      next.add(method);
    }
    setActivePayMethods(next);
  };

  const setAllPayMethodsActive = (active: boolean) => {
    if (active) {
      setActivePayMethods(new Set(payMethodStats.map((m) => m.method)));
    } else {
      setActivePayMethods(new Set());
      setSelection(new Map());
    }
  };

  const toggleInvoice = (row: HanaOpenCustomerInvoiceRow) => {
    if (!row.BpIban) return;
    const next = new Map(selection);
    if (next.has(row.DocEntry)) {
      next.delete(row.DocEntry);
    } else {
      next.set(row.DocEntry, {
        amount: row.PendingAmount,
        remittance: `Fra ${row.NumAtCard || row.DocNum}`,
      });
    }
    setSelection(next);
  };

  const updateSelection = (docEntry: number, patch: Partial<{ amount: number; remittance: string }>) => {
    const cur = selection.get(docEntry);
    if (!cur) return;
    const next = new Map(selection);
    next.set(docEntry, { ...cur, ...patch });
    setSelection(next);
  };

  const toggleSelectAllPayable = () => {
    if (!visibleInvoices) return;
    const payable = visibleInvoices.filter((r) => r.BpIban);
    if (payable.length > 0 && payable.every((r) => selection.has(r.DocEntry))) {
      const next = new Map(selection);
      for (const r of payable) next.delete(r.DocEntry);
      setSelection(next);
    } else {
      const next = new Map(selection);
      for (const r of payable) {
        if (next.has(r.DocEntry)) continue;
        next.set(r.DocEntry, {
          amount: r.PendingAmount,
          remittance: `Fra ${r.NumAtCard || r.DocNum}`,
        });
      }
      setSelection(next);
    }
  };

  const totalSelected = useMemo(() => {
    let t = 0;
    for (const [, v] of selection) t += Number(v.amount) || 0;
    return Math.round(t * 100) / 100;
  }, [selection]);

  // Modalidad (CORE/B2B) y banco propio (OpymDflIban) de las facturas
  // seleccionadas. Si mezclan, hay que separar en 2 remesas.
  const selectionMix = useMemo(() => {
    if (!invoices) return { schemes: [] as string[], banks: [] as string[] };
    const schemes = new Set<string>();
    const banks = new Set<string>();
    for (const [docEntry] of selection) {
      const inv = invoices.find((i) => i.DocEntry === docEntry);
      if (!inv) continue;
      schemes.add(inv.DirectDbt);
      banks.add(inv.OpymDflIban);
    }
    return { schemes: Array.from(schemes), banks: Array.from(banks) };
  }, [invoices, selection]);

  const mixOk = selectionMix.schemes.length <= 1 && selectionMix.banks.length <= 1;

  const generateBatch = async () => {
    if (!selectedCompany || selection.size === 0 || !mixOk || selectionMix.schemes.length === 0) return;
    const scheme = selectionMix.schemes[0] as "CORE" | "B2B";
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const lines = Array.from(selection.entries()).map(([docEntry, v]) => ({
        invoiceDocEntry: docEntry,
        amount: v.amount,
        remittanceInfo: v.remittance,
      }));
      const r = await createDirectDebitBatch({
        companyId: selectedCompany,
        scheme,
        requestedCollectionDate: collectionDate,
        lines,
      });
      setNotice(
        `✓ Fichero generado: ${r.batch.filename} · ${r.batch.lines.length} facturas · total ${eur(r.batch.totalAmount)} · modalidad ${r.batch.scheme}`,
      );
      window.open(directDebitBatchFileUrl(r.batch.id), "_blank");
      await Promise.all([loadInvoices(), reloadBatches()]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const groupedByCard = useMemo(() => {
    if (!visibleInvoices) return [] as Array<{ cardCode: string; cardName: string; bpIban: string; rows: HanaOpenCustomerInvoiceRow[] }>;
    const map = new Map<string, { cardCode: string; cardName: string; bpIban: string; rows: HanaOpenCustomerInvoiceRow[] }>();
    for (const r of visibleInvoices) {
      const cur = map.get(r.CardCode);
      if (cur) cur.rows.push(r);
      else map.set(r.CardCode, { cardCode: r.CardCode, cardName: r.CardName, bpIban: r.BpIban, rows: [r] });
    }
    return Array.from(map.values()).sort((a, b) => {
      if (!a.bpIban && b.bpIban) return 1;
      if (a.bpIban && !b.bpIban) return -1;
      return a.cardName.localeCompare(b.cardName, "es");
    });
  }, [visibleInvoices]);

  return (
    <>
      <section className="card">
        <div className="card-head">
          <div>
            <h2>Cobros por domiciliación</h2>
            <p className="meta">
              Selecciona empresa y fecha de cobro, marca las facturas de cliente a
              domiciliar y genera el fichero SEPA de adeudo directo (pain.008.001.02).
              Todas las facturas del fichero deben compartir modalidad (CORE/B2B) y
              banco propio del método — si mezclas, usa los chips para separar.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <label>
              Empresa:{" "}
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                disabled={busy || companies.length === 0}
              >
                {companies.length === 0 && <option>Sin empresas SAP</option>}
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.alias || c.name}</option>
                ))}
              </select>
            </label>
            <label>
              Fecha de cobro:{" "}
              <input
                type="date"
                value={collectionDate}
                onChange={(e) => setCollectionDate(e.target.value)}
                disabled={busy}
              />
            </label>
            <button type="button" onClick={loadInvoices} disabled={busy || !selectedCompany}>
              {busy ? "Cargando…" : "↻ Cargar facturas abiertas"}
            </button>
          </div>
        </div>

        {error && <p className="error">{error}</p>}
        {notice && (
          <pre
            style={{
              whiteSpace: "pre-wrap",
              background: "rgba(80,200,120,0.08)",
              padding: 8,
              borderRadius: 4,
              margin: "8px 0",
            }}
          >
            {notice}
          </pre>
        )}

        {invoices !== null && invoices.length === 0 && (
          <p className="meta">✓ No hay facturas de cliente abiertas con adeudo directo (CORE/B2B) para esta empresa.</p>
        )}

        {invoices && invoices.length > 0 && (
          <>
            {payMethodStats.length > 1 && (
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  alignItems: "center",
                  margin: "12px 0 4px 0",
                  flexWrap: "wrap",
                }}
              >
                <span className="meta" style={{ marginRight: 4 }}>Método cobro:</span>
                {payMethodStats.map(({ method, scheme, count }) => {
                  const active = activePayMethods.has(method);
                  return (
                    <button
                      key={method}
                      type="button"
                      onClick={() => togglePayMethod(method)}
                      disabled={busy}
                      title={active ? "Click para OCULTAR estas facturas" : "Click para MOSTRAR estas facturas"}
                      style={{
                        padding: "2px 10px",
                        borderRadius: 12,
                        fontSize: "0.85em",
                        border: `1px solid ${active ? "#4a8" : "#ccc"}`,
                        background: active ? "rgba(80,180,120,0.15)" : "rgba(150,150,150,0.08)",
                        color: active ? "#282" : "#888",
                        cursor: "pointer",
                        textDecoration: active ? "none" : "line-through",
                      }}
                    >
                      {method} ({scheme}) ({count})
                    </button>
                  );
                })}
                <button type="button" onClick={() => setAllPayMethodsActive(true)} disabled={busy} style={{ fontSize: "0.8em", padding: "2px 8px" }}>
                  Todos
                </button>
                <button type="button" onClick={() => setAllPayMethodsActive(false)} disabled={busy} style={{ fontSize: "0.8em", padding: "2px 8px" }}>
                  Ninguno
                </button>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "12px 0", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={toggleSelectAllPayable}
                disabled={busy || !visibleInvoices || visibleInvoices.length === 0}
              >
                {selection.size > 0 ? "Deseleccionar" : "Seleccionar todas (con IBAN)"}
              </button>
              <span className="meta">
                {selection.size} seleccionada(s) · Total <strong>{eur(totalSelected)}</strong>
                {visibleInvoices && invoices && visibleInvoices.length !== invoices.length && (
                  <> · Mostrando {visibleInvoices.length} de {invoices.length} facturas</>
                )}
              </span>
              <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                <button type="button" onClick={resetColWidths} disabled={busy} title="Restaurar el ancho por defecto de todas las columnas" style={{ fontSize: "0.85em" }}>
                  ↔ Restaurar anchos
                </button>
                <button
                  type="button"
                  onClick={generateBatch}
                  disabled={busy || selection.size === 0 || !mixOk}
                >
                  📄 Generar fichero SEPA DD ({selection.size})
                </button>
              </div>
            </div>

            {!mixOk && selection.size > 0 && (
              <p className="meta" style={{ margin: "0 0 12px 0", padding: 8, background: "rgba(255,80,80,0.10)", borderRadius: 4, color: "#a33" }}>
                ⚠ La selección mezcla {selectionMix.schemes.length > 1 ? "modalidades distintas (CORE/B2B)" : ""}
                {selectionMix.schemes.length > 1 && selectionMix.banks.length > 1 ? " y " : ""}
                {selectionMix.banks.length > 1 ? "bancos propios distintos" : ""}. Deselecciona hasta dejar un solo grupo homogéneo, o usa los chips de método de cobro para filtrar.
              </p>
            )}

            {visibleInvoices && visibleInvoices.length === 0 && (
              <p className="meta" style={{ margin: "12px 0", padding: 8, background: "rgba(255,180,50,0.10)", borderRadius: 4 }}>
                No hay facturas visibles con los métodos de cobro actualmente activos.
                Activa algún chip de arriba para verlas.
              </p>
            )}

            <div style={{ overflowX: "auto", width: "100%" }}>
              <table className="data compact" style={{ whiteSpace: "nowrap", width: "100%", tableLayout: "fixed" }}>
                <colgroup>
                  {INVOICE_COLUMNS.map((col) => (
                    <col key={col.id} style={{ width: `${columnWidths[col.id]}px` }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    {INVOICE_COLUMNS.map((col) => (
                      <ResizableTh key={col.id} width={columnWidths[col.id]} onResize={(w) => setColWidth(col.id, w)} align={col.alignRight ? "right" : "left"}>
                        {col.label}
                      </ResizableTh>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupedByCard.map((g) =>
                    g.rows.map((r, idx) => {
                      const sel = selection.get(r.DocEntry);
                      const hasIban = Boolean(r.BpIban);
                      const isFirst = idx === 0;
                      const cellStyle: React.CSSProperties = { overflow: "hidden", textOverflow: "ellipsis" };
                      const rowTitle = hasIban
                        ? `IBAN deudor: ${formatIban(r.BpIban)}`
                        : "El cliente NO tiene IBAN configurado en SAP (OCRD.DflIBAN). Rellénalo en la ficha del BP para poder domiciliar.";
                      return (
                        <tr
                          key={r.DocEntry}
                          title={rowTitle}
                          style={{ opacity: hasIban ? 1 : 0.6, borderTop: isFirst ? "1px solid rgba(120,150,200,0.25)" : undefined }}
                        >
                          <td style={cellStyle}>
                            <input type="checkbox" checked={Boolean(sel)} disabled={!hasIban || busy} onChange={() => toggleInvoice(r)} />
                          </td>
                          <td style={cellStyle}>
                            {isFirst ? (
                              <>
                                <span style={{ fontWeight: 600 }}>{r.CardName}</span>{" "}
                                <span style={{ fontSize: "0.85em", color: "#888" }}>({r.CardCode})</span>
                                {!hasIban && (
                                  <span style={{ marginLeft: 8, color: "#c33", fontSize: "0.85em", fontWeight: 600 }}>
                                    ⚠ Sin IBAN configurado
                                  </span>
                                )}
                              </>
                            ) : (
                              <>
                                <span style={{ color: "#aaa" }}>↳ mismo</span>
                                {!hasIban && (
                                  <span style={{ marginLeft: 8, color: "#c33", fontSize: "0.85em", fontWeight: 600 }}>
                                    ⚠ Sin IBAN
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                          <td style={cellStyle}>{r.DocNum}</td>
                          <td style={cellStyle}>{r.NumAtCard || <span style={{ color: "#aaa" }}>—</span>}</td>
                          <td style={cellStyle}>{r.DocDate}</td>
                          <td style={cellStyle}>{r.DocDueDate}</td>
                          <td style={{ ...cellStyle, fontSize: "0.85em", color: "#666" }}>
                            {r.PayMethod || <span style={{ color: "#aaa" }}>—</span>}
                          </td>
                          <td style={cellStyle}>
                            <span
                              style={{
                                fontSize: "0.8em",
                                padding: "1px 6px",
                                borderRadius: 8,
                                background: r.DirectDbt === "B2B" ? "rgba(150,100,220,0.15)" : "rgba(80,160,255,0.15)",
                                color: r.DirectDbt === "B2B" ? "#75c" : "#38a",
                              }}
                            >
                              {r.DirectDbt}
                            </span>
                          </td>
                          <td style={{ ...cellStyle, textAlign: "right" }}>{eur(r.DocTotal)}</td>
                          <td style={{ ...cellStyle, textAlign: "right" }}>
                            <strong>{eur(r.PendingAmount)}</strong>
                          </td>
                          <td style={{ ...cellStyle, textAlign: "right" }}>
                            {sel ? (
                              <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                max={r.PendingAmount}
                                value={sel.amount}
                                onChange={(e) => updateSelection(r.DocEntry, { amount: Number(e.target.value) })}
                                style={{ width: "95%", textAlign: "right" }}
                              />
                            ) : (
                              <span style={{ color: "#aaa" }}>—</span>
                            )}
                          </td>
                          <td style={cellStyle}>
                            {sel ? (
                              <input
                                type="text"
                                value={sel.remittance}
                                maxLength={140}
                                onChange={(e) => updateSelection(r.DocEntry, { remittance: e.target.value })}
                                style={{ width: "95%" }}
                              />
                            ) : (
                              <span style={{ color: "#aaa" }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    }),
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <BatchesHistory batches={batches} onChange={reloadBatches} />
    </>
  );
}

function BatchesHistory({ batches, onChange }: { batches: DirectDebitBatch[]; onChange: () => Promise<void> }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const doPost = async (id: string) => {
    if (!confirm("¿Contabilizar en SAP los efectos de cobro pendientes de este fichero?")) return;
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const r = await postDirectDebitBatch(id);
      const fails = r.results.filter((x) => !x.ok).map((x) => `  • Fra ${x.invoiceDocNum}: ${x.error}`).join("\n");
      setMsg(`Contabilización: ${r.okCount} OK, ${r.failCount} fallo(s).` + (fails ? `\nErrores:\n${fails}` : ""));
      await onChange();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async (id: string) => {
    if (!confirm("¿Anular este fichero? Solo posible si no hay efectos ya contabilizados.")) return;
    setBusy(true);
    setErr(null);
    try {
      await cancelDirectDebitBatchApi(id);
      await onChange();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (id: string) => {
    if (!confirm("¿Borrar definitivamente este fichero del histórico?")) return;
    setBusy(true);
    setErr(null);
    try {
      await deleteDirectDebitBatchApi(id);
      await onChange();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" style={{ marginTop: 16 }}>
      <div className="card-head">
        <div>
          <h2>Remesas de domiciliación</h2>
          <p className="meta">
            Histórico de ficheros SEPA DD. Contabiliza para crear los efectos en SAP.
            Presentar el fichero al banco y marcar los efectos como cobrados se hace
            directamente en SAP → Gestión bancaria → Efectos.
          </p>
        </div>
      </div>
      {err && <p className="error">{err}</p>}
      {msg && (
        <pre style={{ whiteSpace: "pre-wrap", background: "rgba(80,160,255,0.06)", padding: 8, borderRadius: 4 }}>
          {msg}
        </pre>
      )}
      {batches.length === 0 && <p className="meta">Aún no hay remesas generadas.</p>}
      {batches.length > 0 && (
        <div style={{ overflowX: "auto", width: "100%" }}>
          <table className="data" style={{ whiteSpace: "nowrap", width: "100%" }}>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Fichero</th>
                <th>Modalidad</th>
                <th>Cta emisora</th>
                <th>F. cobro</th>
                <th style={{ textAlign: "right" }}>Total</th>
                <th style={{ textAlign: "right" }}>Facturas</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => {
                const posted = b.lines.filter((l) => l.status === "posted").length;
                const failed = b.lines.filter((l) => l.status === "failed").length;
                const pending = b.lines.filter((l) => l.status === "pending").length;
                const canPost = b.status !== "cancelled" && (pending > 0 || failed > 0);
                const canCancel = b.status === "generated" && posted === 0;
                const canDelete = posted === 0;
                return (
                  <Fragment key={b.id}>
                    <tr>
                      <td>{b.createdAt.slice(0, 16).replace("T", " ")}</td>
                      <td style={{ fontFamily: "monospace", fontSize: "0.85em" }}>{b.filename}</td>
                      <td>{b.scheme}</td>
                      <td>
                        {b.creditorAccountAlias}{" "}
                        <span style={{ fontSize: "0.8em", color: "#888" }}>({formatIban(b.creditorIban)})</span>
                      </td>
                      <td>{b.requestedCollectionDate}</td>
                      <td style={{ textAlign: "right" }}>{eur(b.totalAmount)}</td>
                      <td style={{ textAlign: "right" }}>
                        {b.lines.length}{" "}
                        <span style={{ color: "#888" }}>
                          ({posted}✓ / {failed}✗ / {pending}⏳)
                        </span>
                      </td>
                      <td>{statusBadge(b.status)}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <a href={directDebitBatchFileUrl(b.id)} target="_blank" rel="noreferrer">
                          <button type="button" disabled={busy}>⬇ XML</button>
                        </a>{" "}
                        <button type="button" onClick={() => setExpandedId(expandedId === b.id ? null : b.id)} disabled={busy}>
                          {expandedId === b.id ? "▲ Cerrar" : "▼ Detalle"}
                        </button>{" "}
                        {canPost && (
                          <button type="button" onClick={() => doPost(b.id)} disabled={busy}>
                            Contabilizar
                          </button>
                        )}{" "}
                        {canCancel && (
                          <button type="button" onClick={() => doCancel(b.id)} disabled={busy}>
                            Anular
                          </button>
                        )}{" "}
                        {canDelete && (
                          <button type="button" className="danger" onClick={() => doDelete(b.id)} disabled={busy}>
                            Borrar
                          </button>
                        )}
                      </td>
                    </tr>
                    {expandedId === b.id && (
                      <tr>
                        <td colSpan={9} style={{ background: "rgba(120,150,200,0.05)" }}>
                          <BatchDetail batch={b} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function BatchDetail({ batch }: { batch: DirectDebitBatch }) {
  return (
    <div style={{ padding: 12 }}>
      <p className="meta" style={{ margin: "0 0 8px 0" }}>
        MsgId SEPA <code>{batch.sepaMessageId}</code> · Acreedor <code>{batch.creditorSchemeId}</code> · Generado por {batch.createdBy}
        {" · "}Actualizado {batch.updatedAt.slice(0, 16).replace("T", " ")}
      </p>
      <div style={{ overflowX: "auto", width: "100%" }}>
        <table className="data compact" style={{ whiteSpace: "nowrap", width: "100%" }}>
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Nº fra SAP</th>
              <th>Ref. cliente</th>
              <th style={{ textAlign: "right" }}>Importe</th>
              <th>Concepto</th>
              <th>IBAN deudor</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {batch.lines.map((l) => (
              <tr key={l.id}>
                <td>
                  {l.cardName} <span style={{ fontSize: "0.8em", color: "#888" }}>({l.cardCode})</span>
                </td>
                <td>{l.invoiceDocNum}</td>
                <td>{l.invoiceNumAtCard || "—"}</td>
                <td style={{ textAlign: "right" }}>{eur(l.amount)}</td>
                <td>{l.remittanceInfo}</td>
                <td style={{ fontFamily: "monospace", fontSize: "0.85em" }}>{formatIban(l.debtorIban)}</td>
                <td>{lineStatusBadge(l)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {batch.lines.some((l) => l.lastError) && (
        <div style={{ marginTop: 8, fontSize: "0.9em" }}>
          <strong>Avisos / errores:</strong>
          <ul style={{ margin: "4px 0 0 20px" }}>
            {batch.lines
              .filter((l) => l.lastError)
              .map((l) => (
                <li key={l.id}>
                  Fra {l.invoiceDocNum} · {l.cardName}: {l.lastError}
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function lineStatusBadge(l: DirectDebitBatchLine): JSX.Element {
  if (l.status === "posted") {
    return (
      <span style={{ color: "#3a3" }}>
        ✓ Efecto {l.sapBoeNum ?? l.sapBoeKey ?? "?"}
      </span>
    );
  }
  if (l.status === "failed") {
    return (
      <span style={{ color: "#c33" }} title={l.lastError ?? ""}>
        ✗ Error
      </span>
    );
  }
  return <span style={{ color: "#888" }}>⏳ Pendiente</span>;
}

function eur(n: number): string {
  return n.toLocaleString("es-ES", { style: "currency", currency: "EUR" });
}

function formatIban(iban: string): string {
  const s = (iban ?? "").replace(/\s+/g, "").toUpperCase();
  return s.replace(/(.{4})/g, "$1 ").trim();
}

function statusBadge(status: DirectDebitBatch["status"]): JSX.Element {
  const map: Record<DirectDebitBatch["status"], { label: string; color: string; bg: string }> = {
    generated: { label: "Generado", color: "#555", bg: "rgba(120,120,120,0.12)" },
    partially_posted: { label: "Parcial", color: "#a60", bg: "rgba(255,180,50,0.15)" },
    posted: { label: "Contabilizado", color: "#282", bg: "rgba(80,200,120,0.15)" },
    cancelled: { label: "Anulado", color: "#a33", bg: "rgba(200,80,80,0.12)" },
  };
  const s = map[status];
  return (
    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: "0.85em", color: s.color, background: s.bg }}>
      {s.label}
    </span>
  );
}
